import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { translate, type Locale, type MessageKey, type MessageVars } from '@/i18n'
import { fetchPassport, labReportUrl } from './api'
import { PIcon, type PassportIconName } from './icons'
import {
  STEP_KEYS,
  isNotDetected,
  labSummary,
  latestReport,
  normalizeGtin,
  normalizeLotCode,
  splitSeason,
  type LabResult,
  type Passport,
  type PassportView,
  type WarningOutcome,
} from './model'
import { usePageMeta } from './usePageMeta'

/*
 * Public Passport (F7.7), V1 boards V1-F7-07a (Passport), 07b (on hold), 07c (recalled) and 07d
 * (not found), plus the not-released page the V1 scope names (and the server's withdrawn and
 * being-prepared answers in the same pattern). Customer only, on his own phone, no login.
 *
 * No scope switch: the page is public, so there is no config scope to read (the phone's ByScope
 * comes from the logged-in roster); V1 is this branch's release, so the V1 pages replace the old
 * ones. Sizes, colours and wording are copied from the boards' inline styles.
 */

const C = {
  paper: '#fbf9f5',
  ink: '#221e1f',
  rule: '#d9d1c3',
  muted: '#5f5852',
  accent: '#6b5e2f',
  wash: '#f6ece0',
  okWash: '#e3eedf',
  ok: '#2f6b3a',
  pouch: '#d8ccb4',
  border: '#8a827a',
  holdWash: '#fbeacf',
  holdInk: '#8a4f12',
  red: '#b3402e',
  infoWash: '#e3ebf4',
  infoInk: '#2f5d8c',
  white: '#ffffff',
} as const

const SANS = "Mukta, 'Noto Sans Devanagari', system-ui, sans-serif"
const SERIF = "Gelasio, Georgia, 'Noto Sans Devanagari', serif"

/** The email the server falls back to (uc-core PASSPORT_CONTACT default). */
const DEFAULT_EMAIL = 'care@uncompromised.in'
/** The boards' helpline and WhatsApp number are placeholders; the server sends neither yet. */
const HELPLINE: { phone: string | null; whatsapp: string | null } = { phone: null, whatsapp: null }

const LANG_KEY = 'ucsc.passport.lang'

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(LANG_KEY)
    if (saved === 'hi' || saved === 'en') return saved
  } catch {
    /* storage unavailable */
  }
  return typeof navigator !== 'undefined' && navigator.language?.startsWith('hi') ? 'hi' : 'en'
}

type T = (key: MessageKey, vars?: MessageVars) => string

interface Ctx {
  t: T
  locale: Locale
  /** The heading face: Gelasio for English as on the boards; Mukta for Hindi (Gelasio has no Devanagari). */
  display: string
}

const asDate = (d: string) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00+05:30` : d)
const intl = (locale: Locale) => (locale === 'hi' ? 'hi-IN' : 'en-IN')

/** "12 Nov 2026". */
function longDate(d: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intl(locale), {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(asDate(d))
}

/** "18 Oct". */
function shortDate(d: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intl(locale), { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }).format(
    asDate(d),
  )
}

function netText(g: number | null | undefined): string | null {
  if (!g) return null
  return g >= 1000 ? `${g / 1000} kg` : `${g} g`
}

/**
 * /p/:code, /p/10/:lot and /p/01/:gtin/10/:lot — the pouch QR's own path forms. The lot is
 * asked of the server even when its shape is wrong, so the server logs the scan as UNKNOWN.
 */
export function PassportPage() {
  const params = useParams()
  const raw = (params.lot ?? params.code ?? '').trim()
  const code = normalizeLotCode(raw) ?? raw.toUpperCase()
  const gtin = normalizeGtin(params.gtin)
  const [locale, setLocale] = useState<Locale>(initialLocale)
  const t: T = (key, vars) => translate(locale, key, vars)
  const ctx: Ctx = { t, locale, display: locale === 'hi' ? SANS : SERIF }
  const query = useQuery({
    queryKey: ['passport', code, gtin],
    queryFn: ({ signal }) => fetchPassport(code, gtin, signal),
    enabled: !!code,
    staleTime: 30_000,
    retry: 1,
  })
  const view: PassportView | null = !code ? { kind: 'NOT_FOUND', code: '', contact: null } : (query.data ?? null)

  usePageMeta(view, t)

  const switchLocale = () => {
    const next: Locale = locale === 'hi' ? 'en' : 'hi'
    setLocale(next)
    try {
      localStorage.setItem(LANG_KEY, next)
    } catch {
      /* ignore */
    }
  }

  return (
    <main
      lang={locale}
      style={{ minHeight: '100dvh', background: C.paper, color: C.ink, fontFamily: SANS, lineHeight: 1.35 }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: `1px solid ${C.rule}`,
            background: C.paper,
          }}
        >
          <a
            href="https://uncompromised.in"
            style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 700, color: C.ink, textDecoration: 'none' }}
          >
            Uncompromised
          </a>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.1em', color: C.accent }}>{t('passport.tag')}</span>
            <button
              type="button"
              onClick={switchLocale}
              lang={locale === 'hi' ? 'en' : 'hi'}
              aria-label={t('passport.switchToLabel')}
              style={{
                height: 36,
                padding: '0 12px',
                borderRadius: 18,
                border: `1.5px solid ${C.border}`,
                background: 'transparent',
                color: C.ink,
                display: 'flex',
                alignItems: 'center',
                fontSize: 15,
                fontWeight: 700,
                fontFamily: SANS,
                cursor: 'pointer',
              }}
            >
              {t('passport.switchTo')}
            </button>
          </span>
        </header>
        {view ? (
          <Body view={view} ctx={ctx} raw={raw} />
        ) : query.isError ? (
          <ErrorState ctx={ctx} onRetry={() => void query.refetch()} />
        ) : (
          <Loading t={t} />
        )}
      </div>
    </main>
  )
}

function Body({ view, ctx, raw }: { view: PassportView; ctx: Ctx; raw: string }) {
  if (view.kind === 'PASSPORT') return <PassportBody code={view.code} p={view.passport} ctx={ctx} />
  if (view.kind === 'NOT_FOUND')
    return <NotFound key={view.code || raw} shown={raw.toUpperCase() || view.code} contact={view.contact} ctx={ctx} />
  return <Warning view={view} ctx={ctx} />
}

// ---------------------------------------------------------------------------------------------
// Board pieces
// ---------------------------------------------------------------------------------------------

const sectionStyle: CSSProperties = {
  padding: '18px 16px',
  borderBottom: `1px solid ${C.rule}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const btnBase: CSSProperties = {
  minHeight: 48,
  borderRadius: 10,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontSize: 16,
  fontWeight: 700,
  boxSizing: 'border-box',
  padding: '0 14px',
  fontFamily: SANS,
  cursor: 'pointer',
  width: '100%',
}
const btnPrimary: CSSProperties = { ...btnBase, background: C.accent, color: C.white, border: 0 }
const btnSecondary: CSSProperties = { ...btnBase, background: C.white, color: C.ink, border: `1.5px solid ${C.border}` }

function H2({ ctx, children }: { ctx: Ctx; children: ReactNode }) {
  return <h2 style={{ margin: 0, fontFamily: ctx.display, fontSize: 22, lineHeight: 1.2, fontWeight: 700 }}>{children}</h2>
}

function SectionHead({ icon, ctx, children }: { icon: PassportIconName; ctx: Ctx; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 18,
          background: C.wash,
          color: C.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PIcon name={icon} />
      </span>
      <H2 ctx={ctx}>{children}</H2>
    </div>
  )
}

/** Label left, bold value right, a rule above (the boards' key–value list). */
function Rows({ rows }: { rows: Array<[string, ReactNode] | null | false> }) {
  return (
    <dl style={{ margin: 0 }}>
      {rows.filter((r): r is [string, ReactNode] => !!r).map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            padding: '6px 0',
            borderTop: `1px solid ${C.rule}`,
            fontSize: 16,
          }}
        >
          <dt style={{ color: C.muted }}>{k}</dt>
          <dd style={{ margin: 0, fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function Tick() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: 11,
        background: C.ok,
        color: C.white,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <PIcon name="ok" size={14} stroke={3} />
    </span>
  )
}

interface ContactWays {
  email: string
  phone: string | null
  whatsapp: string | null
}

function contactWays(serverEmail: string | null): ContactWays {
  return { email: serverEmail || DEFAULT_EMAIL, phone: HELPLINE.phone, whatsapp: HELPLINE.whatsapp }
}

/** Call (primary on warnings), WhatsApp, Email — only the ways we have; the first is primary when asked. */
function ContactButtons({ ways, t, primaryFirst }: { ways: ContactWays; t: T; primaryFirst: boolean }) {
  const items: Array<{ key: string; href: string; icon: PassportIconName | null; text: string }> = []
  if (ways.phone) items.push({ key: 'call', href: `tel:${ways.phone}`, icon: 'call', text: t('passport.contact.call', { phone: ways.phone }) })
  if (ways.whatsapp)
    items.push({
      key: 'wa',
      href: `https://wa.me/${ways.whatsapp.replace(/\D/g, '')}`,
      icon: 'phone',
      text: t('passport.contact.whatsapp', { phone: ways.whatsapp }),
    })
  items.push({ key: 'email', href: `mailto:${ways.email}`, icon: null, text: t('passport.contact.email', { email: ways.email }) })
  return (
    <>
      {items.map((it, i) => (
        <a key={it.key} href={it.href} style={primaryFirst && i === 0 ? btnPrimary : btnSecondary}>
          {it.icon && <PIcon name={it.icon} />}
          {it.text}
        </a>
      ))}
    </>
  )
}

function Loading({ t }: { t: T }) {
  const bar = (h: number, w: string): CSSProperties => ({ height: h, width: w, borderRadius: 10, background: C.wash })
  return (
    <div aria-busy="true" aria-label={t('passport.loading')} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...bar(150, '100%'), borderRadius: 14, background: C.pouch, opacity: 0.5 }} />
      <div style={bar(34, '75%')} />
      <div style={bar(18, '40%')} />
      <div style={bar(110, '100%')} />
    </div>
  )
}

function ErrorState({ ctx, onRetry }: { ctx: Ctx; onRetry: () => void }) {
  const { t } = ctx
  return (
    <section style={{ ...sectionStyle, padding: '26px 16px 18px', borderBottom: 0 }}>
      <h1 style={{ margin: 0, fontFamily: ctx.display, fontSize: 30, lineHeight: 1.15, fontWeight: 700 }}>
        {t('passport.error.title')}
      </h1>
      <span style={{ fontSize: 16, lineHeight: 1.45 }}>{t('passport.error.body')}</span>
      <button type="button" onClick={onRetry} style={btnPrimary}>
        {t('passport.error.retry')}
      </button>
    </section>
  )
}

// ---------------------------------------------------------------------------------------------
// V1-F7-07a · Passport
// ---------------------------------------------------------------------------------------------

const KNOWN_SEASONS = ['KHARIF', 'RABI', 'ZAID'] as const

function seasonText(code: string | null | undefined, t: T): string | null {
  const s = splitSeason(code)
  if (!s) return null
  return (KNOWN_SEASONS as readonly string[]).includes(s.season)
    ? t(`passport.season.${s.season}` as MessageKey, { year: s.year })
    : [s.season.toLowerCase(), s.year].filter(Boolean).join(' ')
}

function stepText(s: Passport['processed']['steps'][number], t: T): string {
  const key = STEP_KEYS[s.step]
  const label = key ? t(`passport.step.${key}` as MessageKey) : s.step
  if (s.processed_alone && s.balance_closed) return t('passport.processed.aloneBalanced', { step: label })
  if (s.processed_alone) return t('passport.processed.alone', { step: label })
  return label
}

function ResultText({ r, t }: { r: LabResult; t: T }) {
  if (isNotDetected(r)) return <>{t('passport.tested.ndLoq', { loq: r.loq, uom: r.uom })}</>
  return <>{r.limit != null ? t('passport.tested.withLimit', { value: r.result, limit: String(r.limit) }) : r.result}</>
}

/** A message with one bold value in it ("…at or above **0.01 mg/kg**, the lowest…"). */
function withBold(text: (mark: string) => string, bold: string): ReactNode {
  const MARK = '\u0001'
  const [pre, post] = text(MARK).split(MARK)
  return (
    <>
      {pre}
      <b>{bold}</b>
      {post}
    </>
  )
}

function PassportBody({ code, p, ctx }: { code: string; p: Passport; ctx: Ctx }) {
  const { t, locale } = ctx
  const [panelOpen, setPanelOpen] = useState(false)
  const report = latestReport(p)
  const lab = report ? labSummary(report) : null
  const net = netText(p.product.net_g)
  const season = seasonText(p.origin.season, t)
  const o = p.origin

  const headline = o.farmer_name
    ? o.farmer_name
    : o.village
      ? o.district
        ? t('passport.origin.villageDistrict', { village: o.village, district: o.district })
        : t('passport.origin.village', { village: o.village })
      : o.district
        ? t('passport.origin.district', { district: o.district })
        : t('passport.origin.oneFarmer')
  const place = o.farmer_name
    ? o.village && o.district
      ? t('passport.origin.villageDistrict', { village: o.village, district: o.district })
      : o.village
        ? t('passport.origin.village', { village: o.village })
        : o.district
          ? t('passport.origin.district', { district: o.district })
          : null
    : null
  const consent = o.farmer_name
    ? t('passport.origin.consentBoth')
    : o.village
      ? t('passport.origin.consentVillage')
      : t('passport.origin.consentNone')

  const boughtPlace = o.village ?? o.district
  const steps = p.processed.steps.filter((s) => STEP_KEYS[s.step] !== 'PACK')
  const timeline: Array<{ date: string | null; text: string }> = [
    {
      date: o.bought_at ?? null,
      text: boughtPlace ? t('passport.processed.bought', { place: boughtPlace }) : t('passport.processed.boughtNoPlace'),
    },
    { date: o.stored_at ?? null, text: t('passport.processed.godown') },
    ...steps.map((s) => ({ date: s.date ?? null, text: stepText(s, t) })),
    { date: p.pack_lot.pack_date, text: t('passport.processed.packed') },
    ...(report ? [{ date: report.tested_at ?? null, text: t('passport.processed.tested') }] : []),
    { date: p.pack_lot.released_at ?? null, text: t('passport.processed.released') },
  ]

  const clean = !!lab && lab.pesticides > 0 && lab.pesticidesFound === 0
  const loqText = lab?.loq ? `${lab.loq} ${lab.uom ?? ''}`.trim() : null

  return (
    <article data-testid="passport-PASSPORT" style={{ display: 'flex', flexDirection: 'column' }}>
      <section
        style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderBottom: `1px solid ${C.rule}` }}
      >
        <div
          role="img"
          aria-label={t('passport.photo')}
          style={{
            height: 150,
            borderRadius: 14,
            background: C.pouch,
            color: C.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PIcon name="pouch" size={72} stroke={1.2} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ margin: 0, fontFamily: ctx.display, fontSize: 30, lineHeight: 1.15, fontWeight: 700 }}>
            {p.product.name}
          </h1>
          {net && <span style={{ fontSize: 17, color: C.muted }}>{net}</span>}
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px 3px 4px',
            borderRadius: 16,
            background: C.okWash,
            color: C.ok,
            fontWeight: 700,
            fontSize: 15,
            lineHeight: 1.25,
            alignSelf: 'flex-start',
          }}
        >
          <Tick />
          {p.pack_lot.released_at
            ? t('passport.released', { date: longDate(p.pack_lot.released_at, locale) })
            : t('passport.releasedNoDate')}
        </span>
        <Rows
          rows={[
            [t('passport.row.lotCode'), code],
            [t('passport.row.packed'), longDate(p.pack_lot.pack_date, locale)],
            [t('passport.row.bestBefore'), longDate(p.pack_lot.best_before, locale)],
          ]}
        />
        <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.4 }}>{t('passport.checkCode')}</span>
      </section>

      {report && lab && (
        <section style={sectionStyle}>
          <SectionHead icon="flask" ctx={ctx}>
            {clean ? t('passport.tested.titleClean') : t('passport.tested.title')}
          </SectionHead>
          <div
            style={{
              borderRadius: 12,
              background: C.okWash,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <b style={{ fontSize: 20, lineHeight: 1.25 }}>
              {lab.pesticides === 0
                ? t('passport.tested.headNoPest', { n: lab.total })
                : clean
                  ? t('passport.tested.headClean', { n: lab.pesticides })
                  : t('passport.tested.headFound', { n: lab.pesticides, found: lab.pesticidesFound })}
            </b>
            {loqText && (
              <span style={{ fontSize: 15, lineHeight: 1.45 }}>
                {withBold((m) => t(clean ? 'passport.tested.loqText' : 'passport.tested.loqFound', { loq: m }), loqText)}
              </span>
            )}
          </div>
          <Rows
            rows={[
              ...lab.rows.map(
                (r): [string, ReactNode] => [r.label, r.result ? <ResultText r={r.result} t={t} /> : t('passport.tested.notDetected')],
              ),
              !!report.lab && [t('passport.tested.lab'), report.lab],
              !!report.accreditation_no && [t('passport.tested.accreditation'), report.accreditation_no],
              !!report.tested_at && [t('passport.tested.date'), longDate(report.tested_at, locale)],
              !!report.report_no && [t('passport.tested.report'), report.report_no],
            ]}
          />
          {lab.total > 0 && (
            <button type="button" aria-expanded={panelOpen} onClick={() => setPanelOpen((v) => !v)} style={btnPrimary}>
              <PIcon name={panelOpen ? 'x' : 'list'} />
              {panelOpen ? t('passport.tested.hidePanel') : t('passport.tested.fullPanel', { n: lab.total })}
            </button>
          )}
          {panelOpen && (
            <div data-testid="passport-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {report.panels.map((pn) => (
                <div key={pn.panel}>
                  <h3 style={{ margin: '4px 0 2px', fontSize: 17, fontWeight: 700 }}>
                    {pn.panel} · {pn.results.length}
                  </h3>
                  <Rows
                    rows={pn.results.map((r): [string, ReactNode] => [r.analyte, <ResultText key={r.analyte} r={r} t={t} />])}
                  />
                </div>
              ))}
            </div>
          )}
          {report.report_available && (
            <a href={labReportUrl(code)} target="_blank" rel="noopener noreferrer" style={btnSecondary}>
              <PIcon name="file" />
              {t('passport.tested.openReport')}
            </a>
          )}
        </section>
      )}

      <section style={sectionStyle}>
        <SectionHead icon="pin" ctx={ctx}>
          {t('passport.origin.title')}
        </SectionHead>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <b style={{ fontSize: 20, lineHeight: 1.25 }}>{headline}</b>
          {place && <span style={{ fontSize: 16 }}>{place}</span>}
        </div>
        <span style={{ fontSize: 16, lineHeight: 1.45 }}>
          {season
            ? t('passport.origin.line', { crop: o.crop, season })
            : t('passport.origin.lineNoSeason', { crop: o.crop })}
        </span>
        <span style={{ fontSize: 13, color: C.muted }}>{consent}</span>
      </section>

      <section style={{ ...sectionStyle, gap: 8 }}>
        <SectionHead icon="field" ctx={ctx}>
          {t('passport.grown.title')}
        </SectionHead>
        {[
          season ? t('passport.grown.season', { season }) : t('passport.grown.field'),
          t('passport.grown.storage'),
          t('passport.grown.bags'),
        ].map((line) => (
          <div key={line} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Tick />
            <span style={{ fontSize: 16, lineHeight: 1.45 }}>{line}</span>
          </div>
        ))}
      </section>

      <section style={sectionStyle}>
        <SectionHead icon="machine" ctx={ctx}>
          {t('passport.processed.title')}
        </SectionHead>
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
          {timeline.map((it, i) => {
            const last = i === timeline.length - 1
            return (
              <li key={i} style={{ display: 'grid', gridTemplateColumns: '62px 14px minmax(0, 1fr)', gap: 10 }}>
                <b style={{ fontSize: 15, lineHeight: 1.4, textAlign: 'right' }}>{it.date ? shortDate(it.date, locale) : ''}</b>
                <span aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 6, background: C.accent, marginTop: 5, flexShrink: 0 }} />
                  {!last && <span style={{ flexGrow: 1, width: 2, background: C.rule }} />}
                </span>
                <span style={{ fontSize: 16, lineHeight: 1.45, paddingBottom: last ? 0 : 12 }}>{it.text}</span>
              </li>
            )
          })}
        </ol>
      </section>

      <footer
        style={{ padding: '18px 16px 22px', display: 'flex', flexDirection: 'column', gap: 8, background: C.wash }}
      >
        <b style={{ fontSize: 16 }}>{t('passport.footer.questions')}</b>
        <ContactButtons ways={contactWays(null)} t={t} primaryFirst={false} />
        <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
          {t('passport.footer.note')} · id.uncompromised.in
        </span>
      </footer>
    </article>
  )
}

// ---------------------------------------------------------------------------------------------
// V1-F7-07b on hold · 07c recalled · not released (and withdrawn, being prepared)
// ---------------------------------------------------------------------------------------------

const WARN: Record<WarningOutcome, { bg: string; fg: string; iconFg: string; icon: PassportIconName }> = {
  HELD: { bg: C.holdWash, fg: C.holdInk, iconFg: C.holdInk, icon: 'hourglass' },
  RECALLED: { bg: C.red, fg: C.white, iconFg: C.red, icon: 'recall' },
  WITHDRAWN: { bg: C.red, fg: C.white, iconFg: C.red, icon: 'x' },
  NOT_RELEASED: { bg: C.infoWash, fg: C.infoInk, iconFg: C.infoInk, icon: 'clock' },
  PASSPORT_PREPARING: { bg: C.wash, fg: C.ink, iconFg: C.accent, icon: 'hourglass' },
}

function Warning({ view, ctx }: { view: Extract<PassportView, { body: unknown }>; ctx: Ctx }) {
  const { t, locale } = ctx
  const w = WARN[view.kind]
  const k = (s: string) => `passport.warn.${view.kind}.${s}` as MessageKey
  const b = view.body
  const product = b.product ? [b.product.name, netText(b.product.net_g)].filter(Boolean).join(' · ') : null
  return (
    <div data-testid={`passport-${view.kind}`} style={{ display: 'flex', flexDirection: 'column' }}>
      <section
        role="alert"
        style={{ background: w.bg, color: w.fg, padding: '22px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <span
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            background: C.white,
            color: w.iconFg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PIcon name={w.icon} size={38} stroke={2.2} />
        </span>
        <h1 style={{ margin: 0, fontFamily: ctx.display, fontSize: 30, lineHeight: 1.15, fontWeight: 700 }}>{t(k('title'))}</h1>
        <span style={{ fontSize: 17, lineHeight: 1.45 }}>{t(k('body'))}</span>
      </section>
      <section style={{ ...sectionStyle, gap: 6 }}>
        <Rows
          rows={[
            !!product && [t('passport.row.product'), product],
            [t('passport.row.lotCode'), view.code],
            !!b.pack_date && [t('passport.row.packed'), longDate(b.pack_date, locale)],
          ]}
        />
        {b.since && <span style={{ fontSize: 14, color: C.muted }}>{t(k('since'), { date: longDate(b.since, locale) })}</span>}
      </section>
      <section style={{ ...sectionStyle, gap: 12 }}>
        <H2 ctx={ctx}>{t('passport.warn.whatToDo')}</H2>
        {(['step1', 'step2', 'step3'] as const).map((s, i) => (
          <div key={s} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 14,
                border: `2px solid ${C.ink}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 15,
                boxSizing: 'border-box',
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontSize: 17, lineHeight: 1.45 }}>{t(k(s))}</span>
          </div>
        ))}
      </section>
      <section style={sectionStyle}>
        <H2 ctx={ctx}>{t('passport.warn.contactUs')}</H2>
        <ContactButtons ways={contactWays(view.contact)} t={t} primaryFirst />
      </section>
      <span style={{ padding: '14px 16px', fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
        {t(k('foot'))} · id.uncompromised.in
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// V1-F7-07d · not found
// ---------------------------------------------------------------------------------------------

function NotFound({ shown, contact, ctx }: { shown: string; contact: string | null; ctx: Ctx }) {
  const { t } = ctx
  const navigate = useNavigate()
  const [typed, setTyped] = useState('PL-')
  const [bad, setBad] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const code = normalizeLotCode(typed)
    if (!code) {
      setBad(true)
      return
    }
    navigate(`/p/10/${code}`)
  }

  return (
    <div data-testid="passport-NOT_FOUND" style={{ display: 'flex', flexDirection: 'column' }}>
      <section style={{ ...sectionStyle, padding: '26px 16px 18px' }}>
        <span
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            background: C.wash,
            color: C.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PIcon name="search" size={36} stroke={2.2} />
        </span>
        <h1 style={{ margin: 0, fontFamily: ctx.display, fontSize: 30, lineHeight: 1.15, fontWeight: 700 }}>
          {t('passport.nf.title')}
        </h1>
        {shown && (
          <div style={{ borderRadius: 10, background: C.white, border: `1.5px solid ${C.rule}`, padding: '8px 12px' }}>
            <span style={{ fontSize: 13, color: C.muted }}>{t('passport.nf.opened')}</span>
            <b style={{ display: 'block', fontSize: 22, letterSpacing: '.03em', overflowWrap: 'anywhere' }}>{shown}</b>
          </div>
        )}
        <span style={{ fontSize: 16, lineHeight: 1.45 }}>{t('passport.nf.why')}</span>
      </section>
      <form onSubmit={submit} style={sectionStyle} noValidate>
        <H2 ctx={ctx}>{t('passport.nf.typeTitle')}</H2>
        <label htmlFor="passport-lot" style={{ fontSize: 15, color: C.muted }}>
          {t('passport.nf.typeHint')}
        </label>
        <input
          id="passport-lot"
          aria-label={t('passport.nf.inputLabel')}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value.toUpperCase())
            setBad(false)
          }}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
          aria-invalid={bad}
          style={{
            height: 52,
            borderRadius: 10,
            border: `2px solid ${bad ? C.red : C.accent}`,
            background: C.white,
            padding: '0 14px',
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '.03em',
            boxSizing: 'border-box',
            width: '100%',
            fontFamily: SANS,
            color: C.ink,
          }}
        />
        {bad && (
          <span role="alert" style={{ fontSize: 15, color: C.red, fontWeight: 600 }}>
            {t('passport.nf.badShape')}
          </span>
        )}
        <button type="submit" style={btnPrimary}>
          <PIcon name="search" />
          {t('passport.nf.lookUp')}
        </button>
      </form>
      <section style={sectionStyle}>
        <H2 ctx={ctx}>{t('passport.nf.stillTitle')}</H2>
        <span style={{ fontSize: 16, lineHeight: 1.45 }}>{t('passport.nf.stillBody')}</span>
        <ContactButtons ways={contactWays(contact)} t={t} primaryFirst={false} />
      </section>
      <span style={{ padding: '14px 16px', fontSize: 13, color: C.muted }}>{t('passport.nf.noted')} · id.uncompromised.in</span>
    </div>
  )
}
